import { useAuth0 } from "@auth0/auth0-react";
import React, { useEffect, useRef, useState, useMemo } from "react";
import { PageLayout } from "../components/page-layout";
import { TimestampViewer } from "../components/timestamp-viewer";

// Updated import to use doChatStreamingV2
import {
  doChatStreamingV2,
  getChatHistoryStreaming,
  clearChatHistory
} from "../services/chat.service";

import { Loader, User, Bot, Trash2, Wrench, ChevronRight, ChevronDown } from "lucide-react"; // added Wrench
import TextToHTML from "../components/text-to-html";
import { EventSourceMessage } from "@microsoft/fetch-event-source"; // Type for SSE events

// ActionButton Component
const ActionButton: React.FC<{
  title: string;
  subtitle: string;
  handleAction: (title: string) => void;
}> = ({ title, subtitle, handleAction }) => (
  <button
    onClick={() => handleAction(title)}
    className="bg-[#1a2130] border border-[#242e42] text-[#e8ecf4] p-4 rounded-[10px] flex flex-col items-start justify-center w-full h-32 transition-colors hover:bg-[#1e2636]"
  >
    <span className="text-lg font-semibold">{title}</span>
    <span className="text-sm text-[#9aa5b8]">{subtitle}</span>
  </button>
);

function decodeBase64(base64String: string): string {
  try {
    // `atob` decodes standard Base64 in browsers
    return atob(base64String);
  } catch (error) {
    console.error("Error decoding base64:", error);
    return "Error decoding message";
  }
}

// The shape of a single message from the server
interface StrongifierChatMessage {
  chat_source: string;     // e.g. "System", "AI", "Human"
  chat_message: string;    // The text
  chat_timestamp?: string; // e.g. "2025-02-01T12:34:56Z"
}

export const ChatPage: React.FC = () => {
  // We'll store messages as [role, content, timestamp].
  type ChatTuple = [string, string, string | undefined];

  const [chatHistory, setChatHistory] = useState<ChatTuple[]>([]);
  // Derive groupedChatHistory: each human message followed by its non-human responses
  const groupedChatHistory = useMemo<ChatTuple[][]>(() => {
    const groups: ChatTuple[][] = [];
    chatHistory.forEach(tuple => {
      const isHuman = tuple[0] === 'Human';
      if (isHuman) {
        // start a new group with human message
        groups.push([tuple]);
      } else {
        // append to last group if exists, else start a new group
        if (groups.length > 0) {
          groups[groups.length - 1].push(tuple);
        } else {
          groups.push([tuple]);
        }
      }
    });
    return groups;
  }, [chatHistory]);

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingChatResponse, setIsLoadingChatResponse] = useState(false);
  const [strongifierInput, setStrongifierInput] = useState("");
  const [partialAiResponse, setPartialAiResponse] = useState<string | null>(null);
  // Track expanded tool response panels
  const [expandedToolResponses, setExpandedToolResponses] = useState<Set<string>>(new Set());
  const toggleResponse = (key: string) => {
    setExpandedToolResponses(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // SSE AbortController
  const streamingControllerRef = useRef<AbortController | null>(null);

  const chatContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { getAccessTokenSilently } = useAuth0();

  /****************************************************************
   * 1) The SSE-based chat history loading
   ****************************************************************/
  const loadChatHistory = async () => {
    setIsLoading(true);
    try {
      const accessToken = await getAccessTokenSilently();

      // Abort any existing SSE
      if (streamingControllerRef.current) {
        streamingControllerRef.current.abort();
      }

      // Start streaming the history (this calls `greeting_generator` or similar)
      const controller = getChatHistoryStreaming(
        accessToken,
        handleHistoryMessage,
        handleHistoryError,
        handleHistoryClose
      );
      streamingControllerRef.current = controller;
    } catch (error) {
      console.error(
        `Error: ${
          error instanceof Error ? error.message : "Unknown error occurred"
        }`
      );
      setIsLoading(false);
    }
  };

  /**
   * SSE Handlers for chat history
   */
  const handleHistoryMessage = (evt: EventSourceMessage) => {
    const { event: evtName, data } = evt;
    const decodedData = decodeBase64(data);

    if (evtName === "update") {
      // Some ephemeral update from the server
      // For v1 compatibility, treat it as an AI message w/o a timestamp
      setChatHistory(prev => [...prev, ["AI", decodedData, undefined]]);
    } else if (evtName === "chat_history") {
      // This is the final array of messages from the server
      try {
        // The final data is a JSON array of objects
        // { role: "Human"/"AI"/..., content: "...", timestamp: "..." }
        // or older style w/ role/content only
        const arr = JSON.parse(decodedData) as Array<
          Partial<StrongifierChatMessage> & { role?: string; content?: string }
        >;

        // Convert to our [role, content, timestamp] format
        const finalHistory = arr.map(item => {
          const source = item.chat_source ?? item.role ?? "AI";
          const message = item.chat_message ?? item.content ?? "";
          const timestamp = item.chat_timestamp ?? undefined;
          return [source, message, timestamp] as ChatTuple;
        });

        setChatHistory(finalHistory);
      } catch (err) {
        console.error("Error parsing chat_history JSON:", err);
      }

      setIsLoading(false);

      // Optionally abort SSE if the server is done
      if (streamingControllerRef.current) {
        streamingControllerRef.current.abort();
      }
    } else {
      // Unknown or no event => treat as an AI update w/o timestamp
      setChatHistory(prev => [...prev, ["AI", decodedData, undefined]]);
    }
  };

  const handleHistoryError = (error: unknown) => {
    console.error("SSE error (history):", error);
    console.error(`Error loading chat history: ${String(error)}`);
    setIsLoading(false);

    if (streamingControllerRef.current) {
      streamingControllerRef.current.abort();
    }
  };

  const handleHistoryClose = () => {
    console.log("Chat history SSE closed.");
    setIsLoading(false);
  };

  // Load chat history on mount
  useEffect(() => {
    loadChatHistory();
    return () => {
      if (streamingControllerRef.current) {
        streamingControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isLoading && chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatHistory, isLoading]);

  useEffect(() => {
    if (!isLoading && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "auto" });
    }
  }, [chatHistory, isLoading]);

  /**
   * Clearing the chat
   */
  const handleClearChat = async () => {
    setChatHistory([]);
    setIsLoading(true);
    try {
      const accessToken = await getAccessTokenSilently();
      const { error } = await clearChatHistory(accessToken);
      if (error) {
        console.error(`Error: ${error.message}`);
      } else {
        await loadChatHistory();
      }
    } catch (error) {
      console.error(
        `Error: ${
          error instanceof Error ? error.message : "Unknown error occurred"
        }`
      );
      setIsLoading(false);
    }
  };

  /**
   * Submitting a chat message => calls doChatStreamingV2
   */
  const handleStrongifierSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!strongifierInput.trim()) return;

    const inputToSend = strongifierInput.trim();
    setStrongifierInput("");
    handleChatSubmit(inputToSend);
  };

  const handleAction = async (action: string) => {
    handleChatSubmit(action);
  };

/**
 * When submitting a chat message, we create a new message in local state
 * with a UTC timestamp (so that it displays like the server timestamps).
 */
const handleChatSubmit = async (humanMessage: string) => {
  // Create a local UTC ISO timestamp for the user's message
  const nowIso = new Date().toISOString();

  // Add user’s message to local chat with that timestamp
  setChatHistory(prev => [...prev, ["Human", humanMessage, nowIso]]);

  setIsLoadingChatResponse(true);
  setPartialAiResponse(null);

  try {
    if (streamingControllerRef.current) {
      streamingControllerRef.current.abort();
    }
    const accessToken = await getAccessTokenSilently();

    const controller = doChatStreamingV2(
      accessToken,
      humanMessage,
      handleSSEMessage,
      handleSSEError,
      handleSSEClose
    );
    streamingControllerRef.current = controller;
  } catch (error) {
    console.error(
      `Error: ${
        error instanceof Error ? error.message : "Unknown error occurred"
      }`
    );
    setPartialAiResponse(null);
    setIsLoadingChatResponse(false);
  }
};

  /**
   * SSE message handler for v2 (final or update)
   */
  const handleSSEMessage = (evt: EventSourceMessage) => {
    const { event: evtName, data } = evt;
    const decodedContent = decodeBase64(data);

    // This is one StrongifierChatMessage in JSON
    let parsed: StrongifierChatMessage;
    try {
      parsed = JSON.parse(decodedContent) as StrongifierChatMessage;
    } catch (err) {
      console.error("Error parsing JSON from SSE:", err);
      return;
    }

    const { chat_source, chat_message, chat_timestamp} = parsed;


    /*
    Event names:
    * update: These are mostly status updates, e.g. "Tool <tool_name> started..."
    * toolupdate: Updates from a tool, e.g., "Tool <tool_name> returned..."
    * chatupdatemessage: Incremental updates to the AI response, e.g., "I am going to run a tool..."
    * chatfinalmessage: This is the final AI message, which has a real timestamp"
    
    Example data for threshold tool update:
    SSE event: toolupdate data: {"chat_source": "get_user_threshold_info_tool", "chat_message": "threshold_history=[ThresholdHistoryItem(activity_id='STRAVA-11997963718', activity_date='2024-07-27T22:03:04+00:00', estimated_pace=7.33, estimated_heart_rate=147.0), ThresholdHistoryItem(activity_id='STRAVA-12055366758', activity_date='2024-08-03T20:40:34+00:00', estimated_pace=6.12, estimated_heart_rate=160.6), ThresholdHistoryItem(activity_id='STRAVA-12124588114', activity_date='2024-08-12T00:55:43+00:00', estimated_pace=5.79, estimated_heart_rate=159.3), ThresholdHistoryItem(activity_id='STRAVA-12159298697', activity_date='2024-08-16T01:32:21+00:00', estimated_pace=7.6, estimated_heart_rate=133.5), ThresholdHistoryItem(activity_id='STRAVA-12210088709', activity_date='2024-08-22T00:56:47+00:00', estimated_pace=7.45, estimated_heart_rate=146.5), ThresholdHistoryItem(activity_id='STRAVA-12268596782', activity_date='2024-08-28T22:16:14+00:00', estimated_pace=6.98, estimated_heart_rate=144.4), ThresholdHistoryItem(activity_id='STRAVA-12404823535', activity_date='2024-09-14T05:36:10+00:00', estimated_pace=6.84, estimated_heart_rate=149.8), ThresholdHistoryItem(activity_id='STRAVA-12439052973', activity_date='2024-09-18T05:47:10+00:00', estimated_pace=6.36, estimated_heart_rate=146.0), ThresholdHistoryItem(activity_id='STRAVA-13028059305', activity_date='2024-12-02T05:55:53+00:00', estimated_pace=0.0, estimated_heart_rate=None), ThresholdHistoryItem(activity_id='STRAVA-13090100140', activity_date='2024-12-11T00:21:23+00:00', estimated_pace=6.24, estimated_heart_rate=161.7), ThresholdHistoryItem(activity_id='STRAVA-13699246823', activity_date='2025-02-22T20:19:04+00:00', estimated_pace=6.64, estimated_heart_rate=159.2), ThresholdHistoryItem(activity_id='STRAVA-13857132722', activity_date='2025-03-11T22:25:55+00:00', estimated_pace=6.65, estimated_heart_rate=165.2), ThresholdHistoryItem(activity_id='STRAVA-14843962384', activity_date='2025-06-18T21:41:13+00:00', estimated_pace=6.54, estimated_heart_rate=164.8)] threshold_pace=6.54 threshold_heart_rate=164.8 threshold_pace_activity_id='STRAVA-14843962384' threshold_heart_rate_activity_id='STRAVA-14843962384'", "chat_timestamp": "2025-06-29T20:20:05.867287+00:00"}
    
    */


    console.log("SSE event:", evtName, "data:", decodedContent);

    if (evtName === "update") {
      // Ephemeral update
      setPartialAiResponse(chat_message);
    } else if (evtName === "chatupdatemessage" || evtName === "toolupdate") {

      setPartialAiResponse(null);
      setIsLoadingChatResponse(true);

      // Append to chat with the real timestamp
      setChatHistory(prev => [
        ...prev,
        [chat_source || "AI", chat_message, chat_timestamp]
      ]);

      
    } else if (evtName === "chatfinalmessage") {
      // Final AI message
      setPartialAiResponse(null);
      setIsLoadingChatResponse(false);

      // Append to chat with the real timestamp
      setChatHistory(prev => [
        ...prev,
        [chat_source || "AI", chat_message, chat_timestamp]
      ]);

      // Optionally abort SSE
      if (streamingControllerRef.current) {
        streamingControllerRef.current.abort();
      }
    } else {
      // Unknown event => treat as partial
      console.warn("Unknown SSE event:", evtName);
      setPartialAiResponse(chat_message);
    }
  };

  const handleSSEError = (error: unknown) => {
    console.error("SSE error (v2):", error);
    setIsLoadingChatResponse(false);
    if (streamingControllerRef.current) {
      streamingControllerRef.current.abort();
    }
  };

  const handleSSEClose = () => {
    console.log("SSE stream closed by server or aborted (v2).");
  };

  return (
    <PageLayout>
      <div className="flex flex-col items-center justify-between p-4 text-[#e8ecf4] grow text-2xl">
        <div className="flex flex-col items-center grow">
          {/* Waiting / no-chat Logo */}
          {!isLoading && chatHistory.length === 0 ? (
            <div className="mb-12 grow grid grid-cols-3 grid content-center">
              <svg
                className="w-16 h-16"
                viewBox="0 0 100 100"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M50 10 L90 90 L10 90 Z" fill="white" />
              </svg>
            </div>
          ) : null}

          {/* Chat Response Area */}
          {isLoading ? (
            <div className="mb-12 grow grid grid-cols-3 grid content-center">
              <Loader className="w-12 h-12 text-[#8fa3ff] animate-spin" />
            </div>
          ) : (
            chatHistory.length > 0 && (
              <div ref={chatContainerRef} className="mb-12 grow flex flex-col content-top p-6 space-y-4 overflow-y-auto">
                {/* Existing messages */}
                {groupedChatHistory.map((group, groupIndex) => {
                  const [first, ...responses] = group;
                  const [role, content, timestamp] = first;
                  const isHuman = role === 'Human';
                  return (
                    <React.Fragment key={groupIndex}>
                      {/* First message (human or initial non-human) */}
                      <div className={`flex flex-col ${isHuman ? 'items-end' : 'items-start'}`}>
                        {timestamp && (
                          <div className="text-s text-[#9aa5b8] mb-2 mt-4">
                            <TimestampViewer timestamp={timestamp} />
                          </div>
                        )}
                        <div className={`max-w-3/4 ${isHuman ? 'bg-[#5661f0] text-white rounded-l-lg rounded-br-lg ml-12' : 'bg-[#1a2130] border border-[#242e42] text-[#e8ecf4] rounded-r-lg rounded-bl-lg mr-12'} p-4 shadow-md`}>
                          <div className="flex items-start space-x-2">
                            {isHuman ? (
                              <>
                                <TextToHTML content={content} />
                                <User className="w-6 h-6 text-white/80 mt-1 flex-shrink-0" />
                              </>
                            ) : (
                              <>
                                {role.toLowerCase().includes('tool') ? (
                                  <Wrench className="w-6 h-6 text-[#f0862f] mt-1 flex-shrink-0" />
                                ) : (
                                  <Bot className="w-6 h-6 text-[#8fa3ff] mt-1 flex-shrink-0" />
                                )}
                                <TextToHTML content={content} />
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      {/* Render any non-human responses in this group */}
                      {responses.length > 0 && (
                        <div className="flex flex-col items-start">
                          {/* Display one timestamp for all non-human responses */}
                          {responses[0][2] && (
                            <div className="text-s text-[#9aa5b8] mb-2 mt-4">
                              <TimestampViewer timestamp={responses[0][2]!} />
                            </div>
                          )}
                          <div className="max-w-3/4 bg-[#1a2130] border border-[#242e42] text-[#e8ecf4] rounded-r-lg rounded-bl-lg mr-12 p-4 shadow-md space-y-2">
                            {responses.map(([r2, c2], idx2) => {
                              const isTool = r2.toLowerCase().includes('tool');
                              if (!isTool) {
                                return (
                                  <div key={idx2} className="flex items-start space-x-2">
                                    <Bot className="w-6 h-6 text-[#8fa3ff] mt-1 flex-shrink-0" />
                                    <TextToHTML content={c2} />
                                  </div>
                                );
                              }
                              const key = `${groupIndex}-${idx2}`;
                              const expanded = expandedToolResponses.has(key);
                              return (
                                <div key={key}>
                                  <div
                                    onClick={() => toggleResponse(key)}
                                    className="flex items-start space-x-2 cursor-pointer"
                                  >
                                    {expanded ? (
                                      <ChevronDown className="w-6 h-6 mt-1 flex-shrink-0" />
                                    ) : (
                                      <ChevronRight className="w-6 h-6 mt-1 flex-shrink-0" />
                                    )}
                                    <Wrench className="w-6 h-6 text-[#f0862f] mt-1 flex-shrink-0" />
                                    <span className="font-medium">{r2}</span>
                                  </div>
                                  {expanded && (
                                    <div className="mt-2 ml-6">
                                      <TextToHTML content={c2} />
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}

                {/* Partial AI message + spinner if loading */}
                {isLoadingChatResponse && (
                  <div className="flex flex-col items-start">
                    <div className="bg-[#1a2130] border border-[#242e42] text-[#e8ecf4] rounded-r-lg rounded-bl-lg mr-12 p-4 shadow-md max-w-3/4">
                      <div className="flex items-center space-x-2">
                        <Bot className="w-6 h-6 text-[#8fa3ff]" />
                        {/* only show text if available */}
                        {partialAiResponse && <TextToHTML content={partialAiResponse} />}
                        <Loader className="w-6 h-6 text-[#8fa3ff] animate-spin" />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )
          )}

          {/* Clear Chat Button */}
          {!isLoading && chatHistory.length > 0 ? (
            <button
              onClick={handleClearChat}
              className="mb-6 w-4/5 bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded flex items-center justify-center transition duration-300 ease-in-out"
            >
              <Trash2 className="mr-2" size={20} />
              Clear Chat
            </button>
          ) : null}

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-4 mb-12 w-full">
            <ActionButton
              title="If I run a 29 minute 5k, how fast should I be able to run a half marathon in?"
              subtitle="Race Time Prediction"
              handleAction={handleAction}
            />
            <ActionButton
              title="Estimate My Threshold Speed and Heartrate over the past year"
              subtitle="Calculate Benchmark Run Stats"
              handleAction={handleAction}
            />
            <ActionButton
              title="Analyze my cardio zone distribution over the past month"
              subtitle="Understand Running History"
              handleAction={handleAction}
            />
            <ActionButton
              title="What should my next workout be?"
              subtitle="Optimize Training Plan"
              handleAction={handleAction}
            />
          </div>
        </div>

        {/* Message Strongifier */}
        <div className="w-full max-w-2xl mt-auto">
          <form onSubmit={handleStrongifierSubmit} className="flex items-center">
            <input
              type="text"
              value={strongifierInput}
              onChange={(e) => setStrongifierInput(e.target.value)}
              placeholder="Enter a message to Strongifier"
              className="flex-grow p-3 rounded-l-lg bg-[#1a2130] text-[#e8ecf4] placeholder-[#6b7688] border-2 border-r-0 border-[#242e42] focus:outline-none focus:border-[#5661f0]"
            />
            <button
              type="submit"
              className="bg-[#5661f0] text-white p-3 rounded-r-lg hover:bg-[#4650e0] focus:outline-none focus:ring-2 focus:ring-[#5661f0] focus:ring-opacity-50"
            >
              Send
            </button>
          </form>
        </div>
      </div>
    </PageLayout>
  );
};
